import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  readSubtechniqueOptions, readWorkbookBlock, serializeWorkbookParameter, serializeWorkbookSave, tacticKey,
} from '@bloo-team/api-client'
import type {
  SearchRow, Workbook, WorkbookBlock, WorkbookDoc, WorkbookMetaType, WorkbookParameter, WorkbookVersion,
} from '@bloo-team/api-client'
import { getSession } from '@bloo-team/core-shell'
import { isDemoSession } from '@bloo-team/core-shell'
import { DEMO_ANALYTICS, liveAttackApi } from './coverage'
import { demoAddWorkbook, demoWorkbooks, liveWorkbooksApi } from './detections'

/**
 * Vantage · Detections — the workbook document: load, save, create, KB, lock,
 * export, the signal-block contract, and the demo docs. The workbook page
 * (pages/WorkbookPage.tsx, design B · Tab, #205) reads and writes blocks
 * through the per-kind specs in @bloo-team/api-client `workbook-blocks.ts`,
 * drafts through lib/workbook-draft.ts and runs through lib/workbook-run.ts.
 *
 * The Settings tab edits placement (withDocMeta), parameters (withParameters)
 * and the run mode (lib/workbook-schedule.ts); the History tab lists, views
 * and reverts versions through the version hooks below.
 */

export type { WorkbookBlock, WorkbookDoc }

/* ------------------------------------------------------------------ blocks */

/** Display name per block type; unknown types render as themselves. */
export function blockLabel(b: WorkbookBlock): string {
  switch (b.type) {
    case 'dql': return b.gui ? 'dql · builder' : 'dql'
    case 'sql': return 'sql'
    case 'code': return 'code'
    case 'text': return 'note'
    case 'call': return 'call workbook'
    case 'return': return 'return'
    case 'signal': return 'signal'
    case 'ai': return 'ai'
    case 'visual': return 'visualization'
    case 'notification': return 'notification'
    case 'simulation': return 'simulation'
    default: return b.type
  }
}

/** A block the runner can execute as a search job. */
export function isRunnable(b: WorkbookBlock): boolean {
  return (
    (b.type === 'dql' || b.type === 'sql' || b.type === 'signal' || b.type === 'simulation') &&
    !!b.query.trim()
  )
}

/**
 * What to show in the block body. Code blocks are base64 (legacy `atob`);
 * a decode failure falls back to the raw string rather than throwing —
 * the doc is user content.
 */
export function blockBody(b: WorkbookBlock): string {
  if (b.type === 'code') {
    try {
      return atob(b.code)
    } catch {
      return b.code
    }
  }
  if (b.type === 'text') return b.code
  if (b.type === 'call' || b.type === 'return') {
    const target = [b.path, b.name].filter(Boolean).join(' / ')
    return [target, b.query].filter(Boolean).join('\n')
  }
  return b.query || b.code
}

/** Types that carry meaning even with an empty display body. */
const CONTENT_TYPES = new Set([
  'signal', 'ai', 'visual', 'notification', 'call', 'return', 'simulation',
])

/**
 * Blocks worth rendering — legacy pads the document with empty
 * bookkeeping blocks (the editor shows blocks.length - 2). Authorable
 * types must have a body; content types (signal, visual, …) always show,
 * body or not — hiding them made them look lost.
 */
export function visibleBlocks(doc: WorkbookDoc): WorkbookBlock[] {
  return doc.blocks.filter(
    (b) => CONTENT_TYPES.has(b.type) || blockBody(b).trim() !== '' || isRunnable(b),
  )
}

/* ----------------------------------------------------------------- editing */

/** Types this client can author. */
export const AUTHORABLE_TYPES = ['dql', 'sql', 'text', 'code'] as const
export type AuthorableType = (typeof AUTHORABLE_TYPES)[number]

export function isEditable(b: WorkbookBlock): boolean {
  return (AUTHORABLE_TYPES as readonly string[]).includes(b.type)
}

export function newBlock(type: AuthorableType): WorkbookBlock {
  return { type, query: '', code: '', name: '', path: '', gui: false, raw: {} }
}

/** The editable text of a block — what the editor textarea holds. */
export function editorTextOf(b: WorkbookBlock): string {
  return blockBody(b)
}

/** A block with its editable text replaced (code re-encodes to base64). */
export function withEditorText(b: WorkbookBlock, text: string): WorkbookBlock {
  if (b.type === 'text') return { ...b, code: text }
  if (b.type === 'code') {
    let encoded = text
    try {
      encoded = btoa(text)
    } catch {
      // Non-latin1 source: keep plaintext rather than corrupting it.
    }
    return { ...b, code: encoded }
  }
  return { ...b, query: text }
}

/* ----------------------------------------------------- signal block author */

export interface SignalEntity {
  role: 'Suspect' | 'Target'
  field: string
  value: string
}

/** The detection contract a signal block carries. */
export interface SignalSpec {
  name: string
  /** 0..10 — DetectionScore; severity derives from it (legacy bands). */
  score: string
  confidence: string
  tactic: string
  technique: string
  subtechnique: string
  tags: string[]
  entities: SignalEntity[]
  kb: string
  /** `raw_event_query` — the analyst's raw-events query, `{SuspectHost}`-style
   * placeholders filled from the mapped entities. Optional so a spec built
   * without it leaves the stored query untouched. */
  rawEventQuery?: string
}

/** The legacy score→severity bands (newSignalBlock onSeverityChange). */
/**
 * A notification recipient (to / cc / bcc) is an email address or a `$Field`
 * placeholder filled from the rows (e.g. `$UserEmail`). Returns the problem, or
 * null when valid — the ChipInput `validate` contract (invalid is rejected).
 */
export function recipientProblem(value: string): string | null {
  const v = value.trim()
  const email = /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]+$/
  const field = /^\$[A-Za-z_][A-Za-z0-9_]*$/
  return email.test(v) || field.test(v) ? null : 'Enter an email address or a $Field'
}

export function severityForScore(score: string): string {
  const v = parseInt(score, 10)
  if (Number.isNaN(v)) return ''
  if (v === 10) return 'Critical'
  if (v >= 7) return 'High'
  if (v >= 4) return 'Medium'
  if (v >= 1) return 'Low'
  if (v === 0) return 'None'
  return ''
}

const SIGNAL_FIXED_KEYS = new Set([
  'type', 'DetectionName', 'DetectionConfidence', 'DetectionScore', 'DetectionSeverity',
  'DetectionTactic', 'DetectionTechnique', 'DetectionSubtechnique', 'tag_list',
  'SourceStream', 'Workbook', 'raw_event_query', 'signal_kb', 'is_generated',
])

export function readSignalSpec(b: WorkbookBlock): SignalSpec {
  const r = b.raw
  const str = (x: unknown) => (typeof x === 'string' ? x : typeof x === 'number' ? String(x) : '')
  const entities: SignalEntity[] = []
  for (const [k, v] of Object.entries(r)) {
    if (SIGNAL_FIXED_KEYS.has(k)) continue
    if (k.startsWith('Suspect')) entities.push({ role: 'Suspect', field: k.slice(7), value: str(v) })
    else if (k.startsWith('Target')) entities.push({ role: 'Target', field: k.slice(6), value: str(v) })
  }
  return {
    name: str(r.DetectionName),
    score: str(r.DetectionScore),
    confidence: str(r.DetectionConfidence),
    tactic: str(r.DetectionTactic),
    technique: str(r.DetectionTechnique),
    subtechnique: str(r.DetectionSubtechnique),
    tags: Array.isArray(r.tag_list) ? r.tag_list.filter((t): t is string => typeof t === 'string') : [],
    entities,
    kb: str(r.signal_kb),
    rawEventQuery: str(r.raw_event_query),
  }
}

/**
 * A signal block with its spec applied — raw-merged: old entity keys are
 * stripped, new ones written as '<Role><Field>': value, severity derives
 * from the score, raw_event_query is written when the spec carries it, and
 * the wire-only fields (SourceStream, Workbook, is_generated,
 * DetectionConfidence) ride through untouched.
 */
export function applySignalSpec(b: WorkbookBlock, spec: SignalSpec): WorkbookBlock {
  const raw: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(b.raw)) {
    if (k.startsWith('Suspect') || k.startsWith('Target')) continue
    raw[k] = v
  }
  raw.type = 'signal'
  raw.DetectionName = spec.name
  if ('DetectionConfidence' in b.raw || spec.confidence !== '') raw.DetectionConfidence = spec.confidence
  raw.DetectionScore = spec.score
  raw.DetectionSeverity = severityForScore(spec.score)
  raw.DetectionTactic = spec.tactic
  raw.DetectionTechnique = spec.technique
  raw.DetectionSubtechnique = spec.subtechnique
  raw.tag_list = spec.tags
  raw.signal_kb = spec.kb
  // Written when edited; never ADDED empty to a block that had none (3 of 1,230 captured).
  if (spec.rawEventQuery !== undefined && ('raw_event_query' in b.raw || spec.rawEventQuery !== '')) {
    raw.raw_event_query = spec.rawEventQuery
  }
  for (const e of spec.entities) {
    if (e.field.trim() && e.value.trim()) raw[e.role + e.field.trim()] = e.value.trim()
  }
  return { ...b, raw }
}

/** A signal block is complete when name, score, tactic, technique and at
 * least one full entity mapping are present (legacy submit gate). */
export function signalSpecComplete(spec: SignalSpec): boolean {
  return (
    spec.name.trim() !== '' &&
    spec.score.trim() !== '' &&
    spec.tactic.trim() !== '' &&
    spec.technique.trim() !== '' &&
    spec.entities.some((e) => e.field.trim() && e.value.trim())
  )
}

/* ----------------------------------------------------------- block results */

/** A settled run's result, frozen per block so switching blocks keeps it. */
export interface BlockResult {
  columns: string[]
  rows: SearchRow[]
  total: number
  executionTime: string | null
  ranQuery: string
}

/**
 * A two-column groupby result: one real column, one synthetic count
 * (`*_colN`, the engine's rename). The same detection fabric.search uses
 * to decide a result is chartable.
 */
export function aggFieldsOf(fields: string[]): { value: string; count: string } | null {
  if (fields.length !== 2) return null
  const count = fields.find((f) => /_col\d+$/i.test(f))
  const value = fields.find((f) => !/_col\d+$/i.test(f))
  return count && value ? { value, count } : null
}

/* ---------------------------------------------------------------- doc meta */

/** Stage / profile / folder edits, carried into the save envelope. */
export function withDocMeta(
  doc: WorkbookDoc,
  meta: { stage?: string; profile?: string; folder?: string },
): WorkbookDoc {
  const raw = { ...doc.raw }
  let stage = doc.stage
  let path = doc.path
  if (meta.stage !== undefined) {
    raw.stage = meta.stage
    stage = meta.stage
  }
  if (meta.profile !== undefined) raw.profile = meta.profile || null
  if (meta.folder !== undefined) {
    path = `/${meta.folder.replace(/^\/+|\/+$/g, '')}`
    raw.path = path
  }
  return { ...doc, stage, path, raw }
}

export function profileOf(doc: WorkbookDoc): string {
  return typeof doc.raw.profile === 'string' ? doc.raw.profile : ''
}

/** The document's free-form properties for the read-mode panel. */
export function propertiesOf(doc: WorkbookDoc): Array<[string, string]> {
  const p = doc.raw.properties
  if (typeof p !== 'object' || p === null) return []
  return Object.entries(p as Record<string, unknown>)
    .filter(([, v]) => typeof v === 'string' || typeof v === 'number')
    .map(([k, v]) => [k, String(v)] as [string, string])
}

/* -------------------------------------------------------------- parameters */

/**
 * Parameters into `config.parameters`, each in its stored shape: the keys
 * the console does not edit (fixed, type, query, queryFieldValue) ride
 * through from the parameter's raw form; the rest of `config` is kept.
 */
export function withParameters(doc: WorkbookDoc, parameters: WorkbookParameter[]): WorkbookDoc {
  const config =
    typeof doc.raw.config === 'object' && doc.raw.config !== null
      ? { ...(doc.raw.config as Record<string, unknown>) }
      : {}
  config.parameters = parameters.map(serializeWorkbookParameter)
  return { ...doc, parameters, raw: { ...doc.raw, config } }
}

/** Parameters compared on the stored shape — what a save would write. */
export function parametersWire(parameters: readonly WorkbookParameter[]): string {
  return JSON.stringify(parameters.map(serializeWorkbookParameter))
}

/* -------------------------------------------------------------- meta lists */

/** The captured workbook/meta lists (2026-09-29) — the demo answers these. */
export const DEMO_WORKBOOK_META: Record<WorkbookMetaType, string[]> = {
  stage: ['prod', 'beta', 'dev', 'test'],
  profile: ['Basic', 'Essential', 'Custom'],
}

/** Stage / profile options for the Settings tab (workbook/meta?type=). */
export function useWorkbookMeta(q: { clusterId: string; type: WorkbookMetaType }) {
  const isDemo = isDemoSession()
  return useQuery<string[]>({
    queryKey: ['workbook-meta', q.clusterId, q.type, isDemo ? 'demo' : 'live'],
    queryFn: () =>
      isDemo ? Promise.resolve(DEMO_WORKBOOK_META[q.type]) : liveWorkbooksApi.meta({ clusterId: q.clusterId, type: q.type }),
    staleTime: 60 * 60_000,
    retry: 1,
  })
}

/** A picker's options: the served list, plus the stored value when the list lacks it. */
export function optionsWith(list: readonly string[], stored: string): string[] {
  return stored && !list.includes(stored) ? [stored, ...list] : [...list]
}

/* --------------------------------------------------------------- demo mode */

const DEMO_NOTES: Record<string, string> = {
  'unusual-spike-in-failed-login-attempts':
    'Watches AUTHENTICATION for failure bursts per account. Tuned to fire above 25 failures in 10 minutes; service accounts are suppressed via the suppression rule on this detection.',
  'suspicious-powershell-execution':
    'Flags encoded/downloader PowerShell command lines from WIN-AUDIT process events. Pairs with the Base64 Payload detection for confirmation.',
}

/** Demo edits/creates live here so the flow round-trips without a backend. */
const demoDocOverrides = new Map<string, WorkbookDoc>()

/** Demo version history per workbook, OLDEST first, each with its document. */
const demoHistory = new Map<string, Array<{ meta: WorkbookVersion; doc: WorkbookDoc }>>()

/** Test hook. */
export function resetDemoDocs() {
  demoDocOverrides.clear()
  demoHistory.clear()
}

/** The demo workbook that carries one block of every kind, as a runnable chain. */
export const DEMO_SHOWCASE_ID = 'internal-port-sweep'

const demoBlock = (raw: Record<string, unknown>): WorkbookBlock => readWorkbookBlock(raw) as WorkbookBlock

function demoSignalRaw(w: Workbook): Record<string, unknown> {
  const tactic = w.path.split('/').filter(Boolean).at(-1) ?? ''
  return {
    type: 'signal',
    DetectionName: w.name,
    DetectionScore: '5',
    DetectionSeverity: 'Medium',
    DetectionConfidence: 'Medium',
    DetectionTactic: tactic,
    DetectionTechnique: w.signal_tags[0] ?? '',
    SourceStream: w.stream_flows,
    SuspectHost: 'SrcIP',
    Workbook: `${w.path}/${w.name}`,
    is_generated: false,
    raw_event_query: `stream=${w.stream_flows[0] ?? 'SIGNALS'} where SrcIP='{SuspectHost}' | duration 1d`,
    signal_kb: '',
    tag_list: [],
  }
}

/** Every block kind, in an order where each one has something above to read. */
function demoShowcaseBlocks(w: Workbook): WorkbookBlock[] {
  return [
    { type: 'text', code: 'Showcase: one block of every kind, chained — each block reads the one above it.' },
    { type: 'dql', block: 'GUI', query: 'stream=FIREWALL | duration 1d | limit 100', hasSourceName: false },
    { type: 'dql', query: 'stream=FIREWALL | duration 1d | groupby Action' },
    {
      type: 'visual',
      data: { chart_type: 'bar', widget_name: 'Events by action', config: { type: 'default', key: ['Action', null, null], value: 'count_col1' } },
    },
    { type: 'code', code: btoa('def transform(inward_array):\n    return inward_array\n'), language: 'python-3.8', encoding: 'bytecode' },
    {
      type: 'ai',
      data: {
        contamination_factor: 0.01,
        query_data: {
          stream: 'FIREWALL', entity: '$SrcIP', duration: '1d', filter_list: [],
          feature_list: [{ distinct: '$DstIP' }, { distinct: '$DstPort' }, { count: '*' }],
        },
        result_condition: { only_anomaly: true, return_graph_result: true, feature_contribution: false },
      },
    },
    { type: 'sql', query: 'SELECT SrcIP, DstIP, DstPort FROM FIREWALL LIMIT 20' },
    demoSignalRaw(w),
    {
      type: 'notification',
      email_list: ['soc@example.test'], email_cc_list: [], email_bcc_list: [],
      email_subject: 'Port sweep from $SrcIP', email_body: 'Sweep detected from $SrcIP.', selectedColumns: ['SrcIP'],
      sendMailForAll: true, sendMailForAllColumns: false, sendMailForAllRows: false, topRows: '10',
    },
    { type: 'call', name: 'External scanner fingerprint', path: '/Native/Reconnaissance', query: 'external-scanner-fingerprint' },
    { type: 'return', name: 'External scanner fingerprint', path: '/Native/Reconnaissance', query: 'external-scanner-fingerprint' },
  ].map(demoBlock)
}

/** Demo document: the library row plus a plausible notebook — a note, a
 * runnable DQL over the workbook's first stream (the demo search api answers
 * stream-shaped queries) and a complete signal block. */
export function demoWorkbookDoc(id: string): WorkbookDoc | null {
  return demoDocOverrides.get(id) ?? demoSeedDoc(id)
}

/** The derived document, before any demo edit. */
function demoSeedDoc(id: string): WorkbookDoc | null {
  const w = demoWorkbooks().find((x) => x.id === id)
  if (!w) return null
  const stream = w.stream_flows[0] ?? 'SIGNALS'
  return {
    ...w,
    description: DEMO_NOTES[id] ?? `Detection notebook for ${w.name}.`,
    parameters: [],
    raw: {},
    blocks:
      id === DEMO_SHOWCASE_ID
        ? demoShowcaseBlocks(w)
        : [
            demoBlock({ type: 'text', code: DEMO_NOTES[id] ?? `Hunt notes for ${w.name}.` }),
            demoBlock({ type: 'dql', query: `stream=${stream} | duration 1d | limit 100` }),
            demoBlock(demoSignalRaw(w)),
          ],
  }
}

function bumpVersion(v: string): string {
  const n = versionNumber(v)
  return n !== null ? `v${n + 1}` : 'v2'
}

/** The number in a version, live (`5`, `'5'`) or demo (`'v4'`); null when there is none. */
export function versionNumber(v: string | number | null | undefined): number | null {
  const m = String(v ?? '').trim().match(/^v?(\d+)$/i)
  return m ? Number(m[1]) : null
}

/** Two versions name the same one ('5' = 'v5'). */
export function sameVersion(a: string | number | null | undefined, b: string | number | null | undefined): boolean {
  const x = versionNumber(a)
  const y = versionNumber(b)
  return x !== null ? x === y : String(a ?? '') !== '' && String(a) === String(b ?? '')
}

/** Display form: `v5`. */
export function versionLabel(v: string | number | null | undefined): string {
  const n = versionNumber(v)
  return n !== null ? `v${n}` : String(v ?? '') || 'v—'
}

/* ------------------------------------------------------------------- hooks */

export function useWorkbookDoc(q: { clusterId: string; scopeId: string; id: string | null }) {
  const isDemo = isDemoSession()
  return useQuery<WorkbookDoc | null>({
    queryKey: ['workbook-doc', q.clusterId, q.scopeId, q.id, isDemo ? 'demo' : 'live'],
    queryFn: () =>
      isDemo
        ? Promise.resolve(demoWorkbookDoc(q.id as string))
        : liveWorkbooksApi.detail({ clusterId: q.clusterId, scopeId: q.scopeId, id: q.id as string }),
    enabled: q.id !== null,
    staleTime: 60_000,
    retry: 1,
  })
}

/* ---------------------------------------------------------------- versions */

/**
 * Demo history, seeded as v2 · v3 · v4 (v4 = the derived doc; the older two
 * thinner, marked "as of vN"), then one entry per demo save or revert.
 */
function demoHistoryOf(id: string): Array<{ meta: WorkbookVersion; doc: WorkbookDoc }> | null {
  const hit = demoHistory.get(id)
  if (hit) return hit
  const seed = demoSeedDoc(id)
  if (!seed) {
    // A workbook made in this demo session: its first version is the create.
    const made = demoDocOverrides.get(id)
    if (!made) return null
    const rows = [{ meta: { version: made.version, commit_msg: 'Initial version', version_author: made.author, last_modified_tstamp: made.last_modified_tstamp }, doc: made }]
    demoHistory.set(id, rows)
    return rows
  }
  const older = (version: string): WorkbookDoc => ({
    ...seed,
    version,
    description: `${seed.description} (as of ${version})`,
    blocks: seed.blocks.slice(0, 2),
  })
  const rows = [
    { meta: { version: 'v2', commit_msg: 'Initial import', version_author: 'DNIF', last_modified_tstamp: 1_740_000_000 }, doc: older('v2') },
    { meta: { version: 'v3', commit_msg: 'Add MITRE tags', version_author: 'DNIF', last_modified_tstamp: 1_748_000_000 }, doc: older('v3') },
    { meta: { version: 'v4', commit_msg: 'Tighten threshold; suppress service accounts', version_author: 'gotham-soc', last_modified_tstamp: 1_753_000_000 }, doc: seed },
  ]
  demoHistory.set(id, rows)
  return rows
}

/** A demo save or revert: the doc becomes the latest, and history gains its row. */
function demoCommit(doc: WorkbookDoc, commitMsg: string): void {
  const rows = demoHistoryOf(doc.id)
  const latest = rows?.at(-1)?.meta.version ?? doc.version
  const saved: WorkbookDoc = { ...doc, version: bumpVersion(latest), last_modified_tstamp: Math.floor(Date.now() / 1000) }
  demoDocOverrides.set(doc.id, saved)
  rows?.push({
    meta: { version: saved.version, commit_msg: commitMsg, version_author: 'you', last_modified_tstamp: saved.last_modified_tstamp },
    doc: saved,
  })
}

/** Demo history, newest first (the live `versions` order). */
export function demoWorkbookVersions(id: string): WorkbookVersion[] {
  return (demoHistoryOf(id) ?? []).map((r) => r.meta).reverse()
}

/** One demo version's document, as it was saved. */
export function demoWorkbookVersionDoc(id: string, version: string): WorkbookDoc | null {
  const row = (demoHistoryOf(id) ?? []).find((r) => sameVersion(r.meta.version, version))
  return row ? { ...row.doc, version: row.meta.version } : null
}

export function useWorkbookVersions(q: { clusterId: string; scopeId: string; id: string | null }) {
  const isDemo = isDemoSession()
  return useQuery<WorkbookVersion[]>({
    queryKey: ['workbook-versions', q.clusterId, q.scopeId, q.id, isDemo ? 'demo' : 'live'],
    queryFn: () =>
      isDemo
        ? Promise.resolve(demoWorkbookVersions(q.id as string))
        : liveWorkbooksApi.versions({ clusterId: q.clusterId, scopeId: q.scopeId, id: q.id as string }),
    enabled: q.id !== null,
    staleTime: 60_000,
    retry: 1,
  })
}

/** A historical version's document, for read-only viewing. */
export function useWorkbookVersionDoc(q: {
  clusterId: string
  scopeId: string
  id: string | null
  version: string | null
}) {
  const isDemo = isDemoSession()
  return useQuery<WorkbookDoc | null>({
    queryKey: ['workbook-version-doc', q.clusterId, q.scopeId, q.id, q.version, isDemo ? 'demo' : 'live'],
    queryFn: () =>
      isDemo
        ? Promise.resolve(demoWorkbookVersionDoc(q.id as string, q.version as string))
        : liveWorkbooksApi.versionDoc({
            clusterId: q.clusterId,
            scopeId: q.scopeId,
            id: q.id as string,
            version: q.version as string,
            ssid: getSession()?.ssid,
          }),
    enabled: q.id !== null && q.version !== null,
    staleTime: 5 * 60_000,
    retry: 1,
  })
}

/* ---------------------------------------------------------- taxonomy hooks */

function demoTechniquesFor(tactic: string): string[] {
  const out = new Set<string>()
  for (const [technique, dets] of Object.entries(DEMO_ANALYTICS)) {
    if (dets.some((d) => tacticKey(d.tactic) === tacticKey(tactic))) out.add(technique)
  }
  return [...out].sort((a, b) => a.localeCompare(b))
}

/** Technique names under one tactic, for the signal-block picker. */
export function useTechniquesForTactic(q: {
  clusterId: string
  scopeId: string
  tactic: string | null
}) {
  const isDemo = isDemoSession()
  return useQuery<string[]>({
    queryKey: ['attack-techniques', q.clusterId, q.scopeId, q.tactic, isDemo ? 'demo' : 'live'],
    queryFn: () =>
      isDemo
        ? Promise.resolve(demoTechniquesFor(q.tactic as string))
        : liveAttackApi.techniques({ clusterId: q.clusterId, scopeId: q.scopeId, tactic: q.tactic as string }),
    enabled: q.tactic !== null && q.tactic !== '',
    staleTime: 10 * 60_000,
    retry: 1,
  })
}

/**
 * The sub-technique a signal gets when its technique changes: the technique
 * itself (e.g. "T1592 - Gather Victim Host Information") — readSubtechniqueOptions
 * lists it first. '' when the technique has no options.
 */
export function defaultSubtechnique(options: readonly string[]): string {
  return options[0] ?? ''
}

/** Demo get_subtechnique_data answers, in the captured shape (2026-09-29). */
const DEMO_SUBTECHNIQUES: Record<string, { id: string; subs: Array<[string, string]> }> = {
  'Gather Victim Host Information': {
    id: 'T1592',
    subs: [['T1592.001', 'Hardware'], ['T1592.002', 'Software'], ['T1592.003', 'Firmware'], ['T1592.004', 'Client Configurations']],
  },
  'Command and Scripting Interpreter': {
    id: 'T1059',
    subs: [['T1059.001', 'PowerShell'], ['T1059.003', 'Windows Command Shell'], ['T1059.004', 'Unix Shell']],
  },
  'Network Service Discovery': { id: 'T1046', subs: [] },
  'Active Scanning': {
    id: 'T1595',
    subs: [['T1595.001', 'Scanning IP Blocks'], ['T1595.002', 'Vulnerability Scanning'], ['T1595.003', 'Wordlist Scanning']],
  },
}

export function demoSubtechniquesFor(techniqueName: string): string[] {
  const t = DEMO_SUBTECHNIQUES[techniqueName]
  if (!t) return []
  return readSubtechniqueOptions({
    data: {
      TechniqueID: t.id,
      TechniqueName: techniqueName,
      Subtechniques: t.subs.map(([SubTechniqueID, SubTechniqueName]) => ({ SubTechniqueID, SubTechniqueName })),
    },
  })
}

export function useSubtechniques(q: {
  clusterId: string
  scopeId: string
  techniqueName: string | null
}) {
  const isDemo = isDemoSession()
  return useQuery<string[]>({
    queryKey: ['attack-subtechniques', q.clusterId, q.scopeId, q.techniqueName, isDemo ? 'demo' : 'live'],
    queryFn: () =>
      isDemo
        ? Promise.resolve(demoSubtechniquesFor(q.techniqueName as string))
        : liveWorkbooksApi.subtechniques({
            clusterId: q.clusterId,
            scopeId: q.scopeId,
            techniqueName: q.techniqueName as string,
          }),
    enabled: q.techniqueName !== null && q.techniqueName !== '',
    staleTime: 10 * 60_000,
    retry: 1,
  })
}

const DEMO_TIMEZONES = ['UTC', 'Asia/Kolkata', 'America/New_York', 'Europe/London', 'Australia/Sydney']

export function useTimezones(q: { clusterId: string }) {
  const isDemo = isDemoSession()
  return useQuery<string[]>({
    queryKey: ['timezones', q.clusterId, isDemo ? 'demo' : 'live'],
    queryFn: async () => {
      if (isDemo) return DEMO_TIMEZONES
      const list = await liveWorkbooksApi.timezones(q)
      return list.length ? list : DEMO_TIMEZONES
    },
    staleTime: 60 * 60_000,
    retry: 1,
  })
}

export interface SaveWorkbookInput {
  /** The document to save onto — the LATEST loaded one, so a run-mode
   * change applied meanwhile is kept. */
  doc: WorkbookDoc
  blocks: WorkbookBlock[]
  commitMsg: string
  /** Name/description edits ride along. */
  name?: string
  description?: string
}

/** Save an edited notebook (workbook/edit). Demo mode stores an override
 * doc with a bumped version and a history row, so the edit round-trips
 * visibly. Resolves once the doc and history have refetched. */
export function useSaveWorkbook(q: { clusterId: string; scopeId: string }) {
  const isDemo = isDemoSession()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: SaveWorkbookInput) => {
      const doc: WorkbookDoc = {
        ...input.doc,
        name: input.name ?? input.doc.name,
        description: input.description ?? input.doc.description,
      }
      if (isDemo) {
        demoCommit({ ...doc, blocks: input.blocks }, input.commitMsg)
        return
      }
      await liveWorkbooksApi.save({
        ...q,
        wbkData: serializeWorkbookSave(doc, input.blocks, input.commitMsg),
        ssid: getSession()?.ssid,
      })
    },
    // Returned, so the mutation settles only after the doc has refetched —
    // a save that follows builds on the new latest version.
    onSuccess: (_d, input) =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ['workbook-doc', q.clusterId, q.scopeId, input.doc.id] }),
        qc.invalidateQueries({ queryKey: ['workbook-versions', q.clusterId, q.scopeId, input.doc.id] }),
        qc.invalidateQueries({ queryKey: ['workbooks', q.clusterId, q.scopeId] }),
      ]),
  })
}

export interface CreateWorkbookInput {
  name: string
  folder: string
  /** Starter DQL for the first query block — promote-from-hunt prefills it. */
  query?: string
  /** Starter description — promotion carries the hunt's hypothesis over. */
  description?: string
}

/** Create an empty notebook (workbook/add): a note block + a starter DQL
 * block. Returns the new id when known (demo always; live only when the
 * response carries one). */
export function useCreateWorkbook(q: { clusterId: string; scopeId: string }) {
  const isDemo = isDemoSession()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: CreateWorkbookInput): Promise<string | null> => {
      const path = `/${input.folder.replace(/^\/+|\/+$/g, '')}`
      const starter: WorkbookDoc = {
        id: '',
        name: input.name,
        path,
        author: '',
        type: 'standard',
        stage: 'dev',
        version: 'v1',
        disabled: true,
        is_locked: false,
        is_private: false,
        signal_count: 0,
        signal_last_seen: null,
        signal_tags: [],
        stream_flows: [],
        signal_kb: '',
        last_modified_tstamp: Math.floor(Date.now() / 1000),
        create_tstamp: Math.floor(Date.now() / 1000),
        last_updated_tstamp: null,
        description: input.description ?? '',
        parameters: [],
        raw: {},
        blocks: [
          newBlock('text'),
          { ...newBlock('dql'), query: input.query ?? 'stream=SIGNALS | duration 1d | limit 100' },
        ],
      }
      if (isDemo) {
        const id = `${input.name.toLowerCase().replace(/\W+/g, '-')}-${Date.now() % 10_000}`
        const row: Workbook = { ...starter, id, author: 'you' }
        demoAddWorkbook(row)
        demoDocOverrides.set(id, { ...starter, id, author: 'you' })
        return id
      }
      return liveWorkbooksApi.create({
        ...q,
        wbkData: serializeWorkbookSave(starter, starter.blocks, 'Initial version'),
      })
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['workbooks', q.clusterId, q.scopeId] })
    },
  })
}

/* -------------------------------------------------- KB / lock / export */

/** The first signal block's spec — KB and generation hang off it. */
export function primarySignalSpec(doc: WorkbookDoc): SignalSpec | null {
  const b = doc.blocks.find((x) => x.type === 'signal')
  return b ? readSignalSpec(b) : null
}

const DEMO_KB = `## Detection\nWatches the stream for the pattern this notebook queries.\n\n## Triage\n1. Confirm the suspect entity is not an approved scanner.\n2. Pivot to Search for the raw events.\n3. Suppress a slice from Detections if benign.`

/** Generated documentation for the notebook's detection. */
export function useWorkbookKb(q: {
  clusterId: string
  scopeId: string
  doc: WorkbookDoc | null
  enabled: boolean
}) {
  const isDemo = isDemoSession()
  const spec = q.doc ? primarySignalSpec(q.doc) : null
  return useQuery<{ kbData: string; kbFileUrl: string } | null>({
    queryKey: ['workbook-kb', q.clusterId, q.scopeId, q.doc?.id, q.doc?.version, isDemo ? 'demo' : 'live'],
    queryFn: () => {
      if (isDemo) return Promise.resolve({ kbData: q.doc?.signal_kb || DEMO_KB, kbFileUrl: '' })
      return liveWorkbooksApi.kbData({
        clusterId: q.clusterId,
        scopeId: q.scopeId,
        workbookId: (q.doc as WorkbookDoc).id,
        version: (q.doc as WorkbookDoc).version,
        signalName: spec?.name ?? (q.doc as WorkbookDoc).name,
        streams: (q.doc as WorkbookDoc).stream_flows,
      })
    },
    enabled: q.enabled && q.doc !== null,
    staleTime: 60_000,
    retry: 1,
  })
}

/** Fire AI documentation generation (content managers). Asynchronous
 * server-side — the KB panel refetches after a beat. */
export function useGenerateKb(q: { clusterId: string; scopeId: string }) {
  const isDemo = isDemoSession()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: {
      doc: WorkbookDoc
      user: { name: string; email: string; role: string; userType: string; licenseType: string }
    }) => {
      if (isDemo) return
      const spec = primarySignalSpec(input.doc)
      const queries = input.doc.blocks
        .filter((b) => (b.type === 'dql' || b.type === 'sql') && b.query.trim())
        .map((b) => b.query)
      await liveWorkbooksApi.kbGenerate({
        clusterId: q.clusterId,
        scopeId: q.scopeId,
        workbookId: input.doc.id,
        version: input.doc.version,
        signalName: spec?.name ?? input.doc.name,
        subtechnique: spec?.subtechnique ?? '',
        queries,
        streams: input.doc.stream_flows,
        user: input.user,
      })
    },
    onSuccess: (_d, input) => {
      void qc.invalidateQueries({ queryKey: ['workbook-kb', q.clusterId, q.scopeId, input.doc.id] })
    },
  })
}

/** Lock/unlock one workbook (content managers). */
export function useLockWorkbook(q: { clusterId: string; scopeId: string }) {
  const isDemo = isDemoSession()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: { id: string; lock: boolean }) => {
      if (isDemo) {
        const w = demoWorkbooks().find((x) => x.id === input.id)
        if (w) w.is_locked = input.lock
        const override = demoDocOverrides.get(input.id)
        if (override) demoDocOverrides.set(input.id, { ...override, is_locked: input.lock })
        return
      }
      await liveWorkbooksApi.lockUnlock({ ...q, ids: [input.id], lock: input.lock })
    },
    onSuccess: (_d, input) => {
      void qc.invalidateQueries({ queryKey: ['workbook-doc', q.clusterId, q.scopeId, input.id] })
      void qc.invalidateQueries({ queryKey: ['workbooks', q.clusterId, q.scopeId] })
    },
  })
}

/**
 * Export the workbook. Live: POST /api/entity/download (an x-tar blob —
 * fetched directly, the JSON client cannot carry it). Demo: the document
 * itself as JSON. Either way the browser saves a file.
 */
export async function exportWorkbook(q: {
  clusterId: string
  scopeId: string
  doc: WorkbookDoc
}): Promise<void> {
  const isDemo = isDemoSession()
  let blob: Blob
  let filename: string
  if (isDemo) {
    blob = new Blob([JSON.stringify(q.doc.raw, null, 2)], { type: 'application/json' })
    filename = `${q.doc.name}.json`
  } else {
    const res = await fetch(`/${q.clusterId}/api/entity/download`, {
      method: 'POST',
      headers: {
        'Content-type': 'application/x-tar',
        SSID: getSession()?.ssid ?? '',
      },
      body: JSON.stringify({
        scope_id: q.scopeId,
        download_dict_info: { workbooks: [q.doc.id], dashboards: [], reports: [] },
      }),
    })
    if (!res.ok) throw new Error(`export failed (${res.status})`)
    blob = await res.blob()
    filename = q.doc.name
  }
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', filename)
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}

/** Revert to a version: its content becomes a new latest version. On
 * success the doc and history refetch. Demo mode commits the old version's
 * document as a new one. */
export function useRevertWorkbook(q: { clusterId: string; scopeId: string }) {
  const isDemo = isDemoSession()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: { id: string; version: string }) => {
      if (isDemo) {
        const old = demoWorkbookVersionDoc(input.id, input.version)
        if (!old) throw new Error(`No version ${input.version}.`)
        const current = demoWorkbookDoc(input.id)
        demoCommit({ ...old, is_locked: current?.is_locked ?? old.is_locked }, `Reverted to an older version ${versionNumber(input.version) ?? input.version}.`) // the backend's wording (2026-09-29)
        return
      }
      await liveWorkbooksApi.revert({ ...q, ...input })
    },
    onSuccess: (_d, input) =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ['workbook-doc', q.clusterId, q.scopeId, input.id] }),
        qc.invalidateQueries({ queryKey: ['workbook-versions', q.clusterId, q.scopeId, input.id] }),
        qc.invalidateQueries({ queryKey: ['workbooks', q.clusterId, q.scopeId] }),
      ]),
  })
}
